import { apiClient } from '../utils/api-client';
import type { ClimbLog, ClimbLogResponse, SendType, ClimbStyleType } from '../types/climb-log';
import type { CragSupportPrompt } from '../types/maintainer';

export interface UpdateClimbLogData {
  rating?: number | null;
  grade_opinion?: number | null;
  climb_style?: ClimbStyleType[];
  topped?: boolean;
  send_type?: SendType | null;
  attempts_count?: number | null;
  topped_at?: string | null;
}

/**
 * A saved log, plus whatever the save earned an ask about.
 *
 * The prompt rides on the save rather than being fetched afterwards: the server
 * is the one that knows this was the send that first topped the route, and a
 * second request would be one asked on every rating change too.
 */
export interface ClimbLogSaveResult {
  log: ClimbLog;
  /** Non-null only on a fresh send at a crag somebody looks after. */
  cragSupport: CragSupportPrompt | null;
}

class ClimbLogsService {
  async getClimbLog(climbSlug: string): Promise<ClimbLog | null> {
    try {
      const response = await apiClient.get(
        `/api/climbs/${climbSlug}/ascent_log`,
        { requireAuth: true }
      );
      const data: ClimbLogResponse = await response.json();
      if (!data.data) return null;
      return this.transformResponse(data);
    } catch (error) {
      console.error('Failed to fetch climb log:', error);
      throw error;
    }
  }

  async createOrUpdate(climbSlug: string, data: UpdateClimbLogData): Promise<ClimbLogSaveResult> {
    try {
      const response = await apiClient.post(`/api/climbs/${climbSlug}/ascent_log`, {
        data: {
          type: 'ascent_log',
          attributes: data
        }
      }, { requireAuth: true });
      const responseData: ClimbLogResponse = await response.json();
      return {
        log: this.transformResponse(responseData),
        cragSupport: responseData.meta?.crag_support ?? null,
      };
    } catch (error) {
      console.error('Failed to create/update climb log:', error);
      throw error;
    }
  }

  async update(climbSlug: string, data: UpdateClimbLogData): Promise<ClimbLog> {
    try {
      const response = await apiClient.patch(`/api/climbs/${climbSlug}/ascent_log`, {
        data: {
          type: 'ascent_log',
          attributes: data
        }
      }, { requireAuth: true });
      const responseData: ClimbLogResponse = await response.json();
      return this.transformResponse(responseData);
    } catch (error) {
      console.error('Failed to update climb log:', error);
      throw error;
    }
  }

  async deleteClimbLog(climbSlug: string): Promise<void> {
    try {
      await apiClient.delete(`/api/climbs/${climbSlug}/ascent_log`, { requireAuth: true });
    } catch (error) {
      console.error('Failed to delete climb log:', error);
      throw error;
    }
  }

  private transformResponse(response: ClimbLogResponse): ClimbLog {
    const attrs = response.data!.attributes;
    return {
      id: response.data!.id,
      rating: attrs.rating,
      grade_opinion: attrs.grade_opinion,
      climb_style: attrs.climb_style,
      topped: attrs.topped,
      send_type: attrs.send_type,
      attempts_count: attrs.attempts_count,
      topped_at: attrs.topped_at,
      created_at: attrs.created_at,
      updated_at: attrs.updated_at
    };
  }
}

export const climbLogsService = new ClimbLogsService();
